/* eslint-disable arrow-body-style */
import { FC as ReactFC, useEffect, useMemo, useState } from 'react';

import BlockUi from 'react-block-ui';
import * as intl from 'react-intl-universal';
import { Col, Row } from 'reactstrap';

import StorageKeys from 'constants/StorageKeys';
import AuthStorageService from 'services/storage-services/AuthStorageService';
import MfaType from 'shared/enums/MfaType';
import Status from 'shared/enums/Status';
import EmailOtpIllustration from 'shared/static/img/email-otp.svg';
import GoogleAuthIllustration from 'shared/static/img/google-authenticator.svg';
import EmailOtpThumb from 'shared/static/img/thumbs/email-otp.png';
import GoogleAuthThumb from 'shared/static/img/thumbs/google-authenticator.png';

import MfaDeactivateModal from './mfa-deactivate-modal/MfaDeactivateModal';
import MfaEmailSetupModal from './mfa-email-setup-modal/MfaEmailSetupModal';
import MfaEnabledModal from './mfa-enabled-modal/MfaEnabledModal';
import MfaGaSetupModal from './mfa-ga-setup-modal/MfaGaSetupModal';
import MfaMethodCard from './mfa-method-card/MfaMethodCard';
import MfaMethod from './MfaMethodModels';
import MfaMethodsProps from './MfaMethodsProps';

const INITIAL_QR_CODE = '';
const INITIAL_RECOVERY_CODES = [];

const MfaMethods: ReactFC<MfaMethodsProps> = (props: MfaMethodsProps) => {
  const {
    data,
    onEmailSetupClick,
    onGoogleAuthSetupClick,
    onEmailOtpVerify,
    onGoogleAuthOtpVerify,
    onDeactivateMfaClick,
    onRefetchMfaStatus,
  } = props;

  const [gaSetupQrModalOpen, setGaSetupQrModalOpen] = useState<boolean>(false);
  const [emailSetupModalOpen, setEmailSetupModalOpen] =
    useState<boolean>(false);
  const [mfaEnabledModalOpen, setMfaEnabledModalOpen] =
    useState<boolean>(false);
  const [mfaDeactivateModalOpen, setMfaDeactivateModalOpen] =
    useState<boolean>(false);
  const [qrCode, setQrCode] = useState<string>(INITIAL_QR_CODE);
  const [recoveryCodes, setRecoveryCodes] = useState<Array<string>>(
    INITIAL_RECOVERY_CODES
  );

  useEffect(() => {
    return () => {
      setQrCode(INITIAL_QR_CODE);
      setRecoveryCodes(INITIAL_RECOVERY_CODES);
    };
  }, []);

  const email =
    AuthStorageService.GetItem<string>(StorageKeys.UserNameKey) ?? '';

  const BASE_MFA_METHODS: Omit<MfaMethod, 'isActive' | 'activatedOn'>[] = [
    {
      type: MfaType.GOOGLE_AUTH,
      name: intl.get('LBL_MFA_METHODS_GA_TITLE'),
      description: intl.get('LBL_MFA_METHODS_GA_DESCRIPTION'),
      illustration: GoogleAuthIllustration,
      thumb: GoogleAuthThumb,
    },
    {
      type: MfaType.EMAIL,
      name: intl.get('LBL_MFA_METHODS_EMAIL_TITLE'),
      description: intl.get('LBL_MFA_METHODS_EMAIL_DESCRIPTION'),
      illustration: EmailOtpIllustration,
      thumb: EmailOtpThumb,
    },
  ];

  const mfaMethods: Array<MfaMethod> = useMemo(() => {
    const isMfaEnabled = data?.enabled ?? false;
    const activeMfaType = data?.mfaType ?? null;
    const activationDate = data?.enabledDate ?? null;

    return BASE_MFA_METHODS.map((baseMethod) => {
      const isActive = isMfaEnabled && activeMfaType === baseMethod.type;

      return {
        ...baseMethod,
        isActive,
        activatedOn: isActive ? activationDate : null,
      };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const isAnyMethodActive = useMemo(
    () => mfaMethods.some((method) => method.isActive),
    [mfaMethods]
  );

  const onToggleEmailSetupModal = (): void => {
    setEmailSetupModalOpen((prev) => !prev);
  };

  const onToggleGaSetupQrModal = (): void => {
    setGaSetupQrModalOpen((prev) => {
      if (prev) setQrCode(INITIAL_QR_CODE);
      return !prev;
    });
  };

  const onToggleMfaEnabledModal = (): void => {
    setMfaEnabledModalOpen((prev) => {
      if (prev) setRecoveryCodes(INITIAL_RECOVERY_CODES);
      return !prev;
    });
  };

  const onToggleMfaDeactivateModal = (): void => {
    setMfaDeactivateModalOpen((prev) => !prev);
  };

  const onEmailSetupFinished = async (otp: string): Promise<void> => {
    const { status, recoveryCodes: codes } = await onEmailOtpVerify(otp);
    if (status === Status.Success) {
      setRecoveryCodes(codes);
      setEmailSetupModalOpen(false);
      setMfaEnabledModalOpen(true);
    }
  };

  const onGaSetupFinished = async (otp: string): Promise<void> => {
    const { status, recoveryCodes: codes } = await onGoogleAuthOtpVerify(otp);
    if (status === Status.Success) {
      setRecoveryCodes(codes);
      setGaSetupQrModalOpen(false);
      setMfaEnabledModalOpen(true);
    }
  };

  const onMfaEnabledModalContinue = async (): Promise<void> => {
    setRecoveryCodes([]);
    setMfaEnabledModalOpen(false);
    await onRefetchMfaStatus();
  };

  const handleSetupNow = async (method: MfaMethod): Promise<void> => {
    if (method.type === MfaType.EMAIL) {
      const { status } = await onEmailSetupClick();
      if (status === Status.Success) {
        setEmailSetupModalOpen(true);
      }
    }
    if (method.type === MfaType.GOOGLE_AUTH) {
      const { status, qrCode: code } = await onGoogleAuthSetupClick();
      if (status === Status.Success) {
        setQrCode(code ?? '');
        setGaSetupQrModalOpen(true);
      }
    }
  };

  const handleDeactivateMfa = async (password: string): Promise<void> => {
    const { status } = await onDeactivateMfaClick(password);
    if (status === Status.Success) {
      setMfaDeactivateModalOpen(false);
      await onRefetchMfaStatus();
    }
  };

  return (
    <BlockUi
      tag="div"
      blocking={data.status === Status.Loading}
      className="row dashboard-row"
    >
      <Col>
        <div className="content-box lower-margins">
          <div className="content-box-title d-flex flex-column align-items-start pb-4 pt-2">
            <h3 className="main-title">
              {intl.get('LBL_MFA_METHODS_SECTION_TITLE')}
            </h3>
            <p className="text-14-light text-gray mb-0 mt-2">
              {intl.get('LBL_MFA_METHODS_SECTION_DESCRIPTION')}
            </p>
          </div>
          <Row className="d-flex flex-wrap justify-content-start @container">
            {mfaMethods.map((method: MfaMethod) => {
              const disableSetupButton = !method.isActive && isAnyMethodActive;
              return (
                <MfaMethodCard
                  key={method.type}
                  method={method}
                  disableSetupButton={disableSetupButton}
                  onSetupNow={() => handleSetupNow(method)}
                  onDeactivate={onToggleMfaDeactivateModal}
                />
              );
            })}
          </Row>
        </div>
      </Col>
      <MfaEmailSetupModal
        email={email}
        isOpen={emailSetupModalOpen}
        status={data.status}
        onToggle={onToggleEmailSetupModal}
        onFinishSetup={onEmailSetupFinished}
      />
      <MfaGaSetupModal
        qr={`data:image/png;base64,${qrCode}`}
        isOpen={gaSetupQrModalOpen}
        status={data.status}
        onToggle={onToggleGaSetupQrModal}
        onFinishSetup={onGaSetupFinished}
      />
      <MfaEnabledModal
        isOpen={mfaEnabledModalOpen}
        recoveryCodes={recoveryCodes}
        status={data.status}
        onToggle={onToggleMfaEnabledModal}
        onContinue={onMfaEnabledModalContinue}
      />
      <MfaDeactivateModal
        isOpen={mfaDeactivateModalOpen}
        status={data.status}
        onToggle={onToggleMfaDeactivateModal}
        onDeactivate={handleDeactivateMfa}
      />
    </BlockUi>
  );
};

export default MfaMethods;
